import { levelName } from "building-generation/elements/level/levelTools";
import { checkOrientationWithMode } from "building-generation/elements/streetOrientation";
import { getWindowHeight } from "building-generation/elements/window/windowHeightTools";
import type { Building } from "building-types/building/buildingTypes";
import type {
  WindowKAM,
  WindowKAMOptions,
  WindowKeyAttributes,
  WindowKeyMeasures,
} from "building-types/dataSequences/builtin/WindowKAM";
import type { KeyAttributesAndMeasuresDisplayOptions } from "building-types/dataSequences/measures";
import { buildingEntityPickHandle } from "building-types/entities/buildingEntityPickHandle";
import { WindowMullionType } from "building-types/settings/settingsTypesEnum";
import type { GridWindowMullionSettings, WindowMullionSettings } from "building-types/settings/windowMullionSettings";
import { WindowOperationType, WindowPanelType, WindowShapeTypes } from "building-types/settings/windowTypesEnum";
import type { GeneratedWindowPlacement, MergedWindowSettings, WindowUnit } from "building-types/windows/windowTypes";
import type { Table } from "common/data/table";
import { assertIsNotNullOrUndefined, assertIsNotUndefined } from "common/utils/assertOfType";
import { getMergedWindowSettings } from "doc/elements/window/getMergedWindowSettings";
import { docEntityHandleHash } from "doc/entity/core/handleHash";
import { isCustomMullionType } from "doc/settingsTools";
import {
  keyAttributeNew,
  keyAttributeOptionsNew,
  keyMeasureNew,
  keyMeasureOptionsNew,
} from "doc/utils/dataSequenceTools";
import { HALF_PI, PI, TWO_PI } from "geom/core";
import type { ConfigurableTable } from "lib/house/dataSequences/dataSequenceConfigurableTable";
import {
  DOC_ENTITY_COLUMN_NAME,
  SortBy,
  categoryColumnNew,
  configurableTableNew,
  identifierColumnNew,
  numberColumnNew,
  rowMetadataNew,
  stringColumnNew,
  stringConverterColumnNew,
} from "lib/house/dataSequences/dataSequenceConfigurableTable";
import { delimitedList } from "lib/house/dataSequences/dataSequenceFormatting";

export function makeWindowsTable(
  windowUnits: Table<WindowUnit>,
  getMergedWindowSettings: (window: WindowUnit) => MergedWindowSettings,
  windowScheduleByReportedName?: boolean,
): ConfigurableTable {
  return configurableTableNew<WindowUnit, WindowUnit>({
    iterables: windowUnits,
    requirement: windowFilter,
    columns: [
      categoryColumnNew("Windows"),
      stringColumnNew({
        name: "Tag",
        fieldFunc: (window) => windowTag(window.windowPlacement) as string, // casting here should be fixed.
        sortBy: SortBy.Ascending,
        useInDiff: !!windowScheduleByReportedName,
      }),
      numberColumnNew({
        name: "Width (IN)",
        fieldFunc: (window) => window.windowPlacement.descriptor.width,
        sortBy: SortBy.Ascending,
        hidden: true,
      }),
      numberColumnNew({
        name: "Height (IN)",
        fieldFunc: (window) => getWindowHeight(window.windowPlacement.descriptor),
        sortBy: SortBy.Ascending,
        hidden: true,
      }),
      numberColumnNew({
        name: "Perimeter (IN)",
        fieldFunc: (window) => windowPerimeter(window.windowPlacement),
        sortBy: SortBy.Ascending,
        useInDiff: false,
      }),
      stringColumnNew({
        name: "Operation Type",
        fieldFunc: (window) => {
          const settings = getMergedWindowSettings(window);
          if (!settings) {
            throw new Error("Merged window settings are undefined");
          }
          return settings.operationType ?? WindowOperationType.DOUBLE_HUNG;
        },
        sortBy: SortBy.Ascending,
      }),
      stringColumnNew({
        name: "Vertical Divisions",
        fieldFunc: (window) => window.windowPlacement.descriptor.verticalDivisions,
        sortBy: SortBy.Ascending,
      }),
      stringColumnNew({
        name: "Panel Type",
        fieldFunc: (window) => {
          const settings = getMergedWindowSettings(window);
          assertIsNotNullOrUndefined(settings.materials);
          return settings.materials.panelType;
        },
        sortBy: SortBy.Ascending,
      }),
      stringColumnNew({
        name: "Glazing Type",
        fieldFunc: (window) => {
          const settings = getMergedWindowSettings(window);
          assertIsNotNullOrUndefined(settings.materials);
          return settings.materials.glazingType;
        },
        sortBy: SortBy.Ascending,
      }),
      stringColumnNew({
        name: "Glazing Color",
        fieldFunc: (window) => {
          const settings = getMergedWindowSettings(window);
          assertIsNotNullOrUndefined(settings.materials);
          return settings.materials.glazingColor;
        },
        sortBy: SortBy.Ascending,
      }),
      stringConverterColumnNew({
        name: "Grids",
        fieldFunc: (window) => {
          const settings = getMergedWindowSettings(window);
          assertIsNotNullOrUndefined(settings.mullions);
          return windowGridDetails(window.windowPlacement, settings.mullions);
        },
        stringFormatter: delimitedList(),
      }),
    ],
    identifiers: [
      identifierColumnNew({
        name: DOC_ENTITY_COLUMN_NAME,
        identifierFunc: (window) => docEntityHandleHash(buildingEntityPickHandle(window)),
      }),
    ],
    rowMetadata: rowMetadataNew({
      metadataFunc: (window) => {
        const settings = getMergedWindowSettings(window);
        assertIsNotNullOrUndefined(settings);
        const entityName = windowEntityName(window.windowPlacement, settings);
        return { entityName, entityType: "WINDOW" };
      },
    }),
  });
}

export function windowFilter(windowUnit: WindowUnit, panelType: WindowPanelType): WindowUnit | null {
  const { windowPlacement: window } = windowUnit;
  const { opening, descriptor } = window;

  if (!opening || descriptor.open) {
    return null;
  }

  if (!descriptor.width || !getWindowHeight(descriptor)) {
    return null;
  }

  if (panelType !== WindowPanelType.VENT && panelType !== WindowPanelType.FAUX_SHUTTER) {
    return windowUnit;
  }

  return null;
}

function windowTag(window: GeneratedWindowPlacement): string | undefined {
  const reportedName = window.descriptor.reportedName;
  const tagOverride = window.reportedName;

  return tagOverride ?? reportedName;
}

function windowPerimeter(window: GeneratedWindowPlacement): number {
  //this will need to get more specific with rough opening/window size in mind
  let perimeter = 0;
  const height = getWindowHeight(window.descriptor);
  if (window.descriptor.arched) {
    const minRadius = window.descriptor.width / 2;
    const arcRadius = Math.max(window.descriptor.archRadius ?? minRadius, minRadius);
    const halfWidth = window.descriptor.width / 2;
    const arcAngleRads = Math.asin(halfWidth / arcRadius);
    const arcHeight = arcRadius * (1 - Math.cos(arcAngleRads));
    const arcLen = arcLength(arcRadius, arcAngleRads);

    perimeter += arcLen;
    perimeter += (height - arcHeight) * 2;
    perimeter += window.descriptor.width;
  } else {
    perimeter += height * 2;
    perimeter += window.descriptor.width * 2;
  }

  return perimeter;

  function arcLength(radius: number, centralAngle: number) {
    const z1 = centralAngle / TWO_PI;
    const halfArcLength = TWO_PI * radius * z1;

    return halfArcLength * 2;
  }
}

function windowArea(window: GeneratedWindowPlacement): number | undefined {
  const { archRadius, shape, width, angleVerticalOffset } = window.descriptor;
  const height = getWindowHeight(window.descriptor);
  const minRadius = width / 2;
  switch (shape) {
    case WindowShapeTypes.RECTANGLE_ARCHED_TOP:
      const arcRadius = Math.max(archRadius ?? minRadius, minRadius);
      const arcAngleRads = Math.asin(minRadius / arcRadius);
      const arcHeight = arcRadius * (1 - Math.cos(arcAngleRads));

      const rectHeight = height - arcHeight;
      const rectArea = rectHeight * width;
      const arcArea = minRadius * arcRadius * HALF_PI;
      return rectArea + arcArea;
    case WindowShapeTypes.POLYGONAL:
      const triArea = angleVerticalOffset === undefined ? undefined : (angleVerticalOffset * width) / 2;
      const baseArea = angleVerticalOffset === undefined ? undefined : (height - angleVerticalOffset) * width;
      return triArea === undefined || baseArea === undefined ? undefined : triArea + baseArea;
    case WindowShapeTypes.ELLIPSE:
    case WindowShapeTypes.CIRCLE:
      const maxRadius = height / 2;
      return minRadius * maxRadius * PI;
    case WindowShapeTypes.HALF_ROUND:
      return minRadius * height * HALF_PI;
    case WindowShapeTypes.RECTANGLE:
      return height * width;
  }
}

// shutters, etc. need descriptors (currently only geometry is output), I'd like to leave this plumbing intact
function windowGridDetails(window: GeneratedWindowPlacement, mergedMullionSettings: WindowMullionSettings) {
  const details: string[] = [];

  const gridSettings = mergedMullionSettings as GridWindowMullionSettings;
  if (
    windowGridType(mergedMullionSettings) &&
    (!gridSettings.mullionOrientation ||
      checkOrientationWithMode(window.streetOrientation, gridSettings.mullionOrientation))
  ) {
    if (mergedMullionSettings.isLegacyWindowGrid) {
      details.push("true");
    } else if (isCustomMullionType(mergedMullionSettings)) {
      if (gridSettings.paneOneRadialLayout) {
        details.push("Radial - Custom");
      } else if (gridSettings.paneOneCircularLayout) {
        details.push("Circular - Custom");
      } else {
        details.push("Orthogonal - Custom");
        if (gridSettings.paneOneXLayout || gridSettings.paneOneYLayout) {
          details.push("1");
        }
        if (gridSettings.paneTwoXLayout || gridSettings.paneTwoYLayout) {
          details.push("2");
        }
      }
    } else if (gridSettings.mullionType === WindowMullionType.ORTHOGONAL) {
      const opType = window.operationType ?? window.descriptor.operationType ?? WindowOperationType.DOUBLE_HUNG;
      const paneOneLites = (gridSettings.paneOneXCount || 1) * (gridSettings.paneOneYCount || 1);
      const paneTwoLites = (gridSettings.paneTwoXCount || 1) * (gridSettings.paneTwoYCount || 1);
      opType === "FIXED" ? details.push(`${paneOneLites}`) : details.push(`${paneOneLites} / ${paneTwoLites}`);
    }
  } else {
    details.push("None");
  }

  return details;
}

function windowGridType(mergedMullionSettings: WindowMullionSettings | undefined): WindowMullionType | undefined {
  if (
    mergedMullionSettings &&
    mergedMullionSettings.mullionType &&
    mergedMullionSettings.mullionType !== WindowMullionType.DISABLED
  ) {
    return mergedMullionSettings.mullionType;
  }
  return undefined;
}

export function windowEntityName(window: GeneratedWindowPlacement, mergedWindowSettings: MergedWindowSettings): string {
  const {
    descriptor: { width, verticalDivisions, arched },
  } = window;

  const glazingType = mergedWindowSettings.materials?.glazingType;
  const glazingColor = mergedWindowSettings.materials?.glazingColor;
  const height = getWindowHeight(window.descriptor);

  const details: string[] = [];
  const dims: string = `${width}/${height}`;
  details.push(dims);

  const opType = mergedWindowSettings.operationType ?? WindowOperationType.DOUBLE_HUNG;
  details.push(`T${opType.slice(0, 2)}`);

  if (glazingType && glazingType.length > 0 && glazingColor && glazingColor.length > 0) {
    details.push(`G${glazingType.slice(0, 2)}${glazingColor.slice(0, 2)}`);
  }

  if (windowGridType(mergedWindowSettings?.mullions)) {
    details.push("GMO");
  } else {
    details.push("GMX");
  }

  if (verticalDivisions && verticalDivisions.length > 0) {
    details.push(`M${verticalDivisions.slice(0, 2)}`);
  } else {
    details.push("MXX");
  }

  if (arched) {
    details.push("ARC");
  }

  return delimitedList("-")(details);
}

// Attributes used in procure entityHash
export const DEFINES_WINDOW: (keyof WindowKeyAttributes)[] = [
  "height",
  "width",
  "operationType",
  "glazingType",
  "glazingColor",
  "grids",
  "verticalDivisions",
];

export function makeWindowKAM(
  windowUnit: WindowUnit,
  mergedWindowSettings: MergedWindowSettings,
  buildingLevelNameFn: (levelId?: string) => string,
): WindowKAM {
  const { windowPlacement: window } = windowUnit;
  assertIsNotNullOrUndefined(mergedWindowSettings.materials);
  assertIsNotNullOrUndefined(mergedWindowSettings.mullions);
  const area = windowArea(window);
  assertIsNotUndefined(area);
  return {
    type: "WINDOW",
    attributes: {
      operationType: keyAttributeNew(mergedWindowSettings.operationType ?? WindowOperationType.DOUBLE_HUNG),
      verticalDivisions: keyAttributeNew(window.descriptor.verticalDivisions),
      panelType: keyAttributeNew(mergedWindowSettings.materials.panelType),
      glazingType: keyAttributeNew(mergedWindowSettings.materials.glazingType),
      glazingColor: keyAttributeNew(mergedWindowSettings.materials.glazingColor),
      grids: keyAttributeNew(windowGridDetails(window, mergedWindowSettings.mullions).join(", ")),
      tag: keyAttributeNew(windowTag(window) as string), //casting here should be fixed.
      width: keyAttributeNew(window.descriptor.width.toString(), "INCHES"),
      height: keyAttributeNew(getWindowHeight(window.descriptor).toString(), "INCHES"),
      perimeter: keyAttributeNew(windowPerimeter(window).toString(), "INCHES"),
      area: keyAttributeNew(area.toString(), "SQ IN"),
      floor: keyAttributeNew(buildingLevelNameFn(window.frontRoom?.level?.id)),
      entityName: keyAttributeNew(windowEntityName(window, mergedWindowSettings)),
    },
    measures: {
      width: keyMeasureNew(window.descriptor.width, "INCHES"),
      height: keyMeasureNew(getWindowHeight(window.descriptor), "INCHES"),
      perimeter: keyMeasureNew(windowPerimeter(window), "INCHES"),
      area: keyMeasureNew(area, "SQ IN"),
      count: keyMeasureNew(1),
    },
    handles: [buildingEntityPickHandle(windowUnit)],
  };
}
export const EMPTY_WINDOW_KAM: WindowKAM = {
  type: "WINDOW",
  attributes: {
    operationType: keyAttributeNew(""),
    verticalDivisions: keyAttributeNew(""),
    panelType: keyAttributeNew(""),
    glazingType: keyAttributeNew(""),
    glazingColor: keyAttributeNew(""),
    grids: keyAttributeNew(""),
    tag: keyAttributeNew(""),
    width: keyAttributeNew("", "INCHES"),
    height: keyAttributeNew("", "INCHES"),
    perimeter: keyAttributeNew("", "INCHES"),
    area: keyAttributeNew("", "SQ IN"),
    floor: keyAttributeNew(""),
    entityName: keyAttributeNew(""),
  },
  measures: {
    width: keyMeasureNew(0, "INCHES"),
    height: keyMeasureNew(0, "INCHES"),
    perimeter: keyMeasureNew(0, "INCHES"),
    area: keyMeasureNew(0, "SQ IN"),
    count: keyMeasureNew(0),
  },
  handles: [],
};

export function makeDefaultWindowKAMOptions(): WindowKAMOptions {
  return {
    attributes: {
      tag: keyAttributeOptionsNew({ definesEntity: false }),
      width: keyAttributeOptionsNew({ unit: "INCHES" }),
      height: keyAttributeOptionsNew({ unit: "INCHES" }),
      perimeter: keyAttributeOptionsNew({ definesEntity: false, unit: "INCHES" }),
      entityName: keyAttributeOptionsNew({ definesEntity: false }),
    },
    measures: {
      width: keyMeasureOptionsNew({ unit: "INCHES" }),
      height: keyMeasureOptionsNew({ unit: "INCHES" }),
      perimeter: keyMeasureOptionsNew({ unit: "INCHES" }),
    },
  };
}
export const defaultWindowKAMSort = (a: WindowKAM, b: WindowKAM) => {
  return (
    a.attributes.tag.value.localeCompare(b.attributes.tag.value) ||
    a.attributes.width.value.localeCompare(b.attributes.width.value, undefined, { numeric: true }) ||
    a.attributes.height.value.localeCompare(b.attributes.height.value, undefined, { numeric: true }) ||
    a.attributes.perimeter.value.localeCompare(b.attributes.perimeter.value, undefined, { numeric: true }) ||
    a.attributes.operationType.value.localeCompare(b.attributes.operationType.value) ||
    a.attributes.verticalDivisions.value.localeCompare(b.attributes.verticalDivisions.value) ||
    a.attributes.panelType.value.localeCompare(b.attributes.panelType.value) ||
    a.attributes.glazingType.value.localeCompare(b.attributes.glazingType.value) ||
    a.attributes.glazingColor.value.localeCompare(b.attributes.glazingColor.value) ||
    a.attributes.grids.value.localeCompare(b.attributes.grids.value)
  );
};

/** @deprecated - use the Building Query API instead */
export function buildingWindowKAMs(
  { windowUnits, settings, levels, settingsSets, optionChoices, doc }: Building,
  filter?: (entity: GeneratedWindowPlacement) => boolean,
): WindowKAM[] {
  const result: WindowKAM[] = [];
  for (const itemId in windowUnits) {
    const windowUnit = windowUnits[itemId];
    const { windowPlacement } = windowUnit;
    const mergedWindowSettings = getMergedWindowSettings({
      window: windowPlacement,
      settings: doc.settings,
      descriptor: windowPlacement.descriptor,
      homeSettings: settings,
      optionChoices,
      entitySettings: doc.entitySettings,
      activeSettingsSets: settingsSets,
    });

    assertIsNotNullOrUndefined(mergedWindowSettings.materials);
    const window = windowFilter(windowUnit, mergedWindowSettings.materials.panelType);

    if (window) {
      if (filter && !filter(windowPlacement)) {
        continue;
      }
      result.push(
        makeWindowKAM(
          windowUnit,
          mergedWindowSettings,
          (levelId) => levelName(levels[levelId as string]) as string, // casting should be fixed here, but this is here to preserve the behavior and fix strict nulls.
        ),
      );
    }
  }
  return result;
}

export const WINDOW_DISPLAY_OPTIONS: KeyAttributesAndMeasuresDisplayOptions<WindowKeyAttributes, WindowKeyMeasures> = {
  attributes: {
    entityName: {
      hidden: true,
    },
    area: {
      hidden: true,
    },
  },
};
